import React, { useState, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { getAppointments, updateAppointment } from '../api';
import useAccionesTurno from '../hooks/useAccionesTurno';
import { timeToMinutes, addMinutesToTime } from '../utils/turnoTiempo';
import { ordenarTurnosPorHorario } from '../utils/ordenarTurnos';
import Modal3Opciones from '../components/Modal3Opciones';
import TurnoModals from '../components/TurnoModals';
import TurnoPopover from '../components/TurnoPopover';
import FormularioTurno from './FormularioTurno';

function calcularHoraFin(fecha, horaStr, duracionMin) {
  const [h, m] = horaStr.split(':').map(Number);
  const total = h * 60 + m + duracionMin;
  const fH = Math.floor(total / 60) % 24;
  const fM = total % 60;
  return `${fecha}T${String(fH).padStart(2, '0')}:${String(fM).padStart(2, '0')}:00`;
}

function Calendario({ pacientes, onVerFicha, turnosRefreshSignal, onPacienteActualizado }) {
  const calRef = useRef(null);

  const [citas, setCitas] = useState([]);
  const [errorCita, setErrorCita] = useState('');
  // Último rango visible pedido a FullCalendar (ver handleDatesSet) — hace
  // falta guardarlo para poder re-pedir el mismo rango cuando
  // turnosRefreshSignal cambia, sin depender de que FullCalendar dispare
  // datesSet de nuevo por su cuenta.
  const rangoVisibleRef = useRef(null);

  const [diaSeleccionado, setDiaSeleccionado] = useState('');
  const [prellenado, setPrellenado] = useState(null);

  // Drag & drop: modal 3 opciones para mover
  const [dragPendiente, setDragPendiente] = useState(null);

  // Resize: modal 3 opciones para duración
  const [resizePendiente, setResizePendiente] = useState(null);

  // Popover rápido al hacer click en evento
  const [popover, setPopover] = useState(null);

  // Edición/cancelación/renombrado de turnos (compartido con PendientesDelDia)
  const acciones = useAccionesTurno({ citas, setCitas, onError: setErrorCita, pacientes, onPacienteActualizado });

  // ── Cargar citas cuando FullCalendar cambia el rango visible ──
  const handleDatesSet = async (info) => {
    const start = info.startStr.slice(0, 10);
    const end = info.endStr.slice(0, 10);
    rangoVisibleRef.current = { start, end };
    try {
      const data = await getAppointments({ start, end });
      setCitas(data);
    } catch (err) {
      console.error('Error cargando citas:', err);
    }
  };

  // Turno creado/cancelado desde la bandeja de Google Calendar (link/
  // link-new-patient/mark-as-event, o su undo) mientras este calendario ya
  // estaba montado — no hay otra forma de enterarse, así que se re-pide el
  // mismo rango que ya estaba visible. Se salta en el primer render
  // (rangoVisibleRef todavía null hasta que FullCalendar dispare su propio
  // datesSet inicial).
  useEffect(() => {
    if (!turnosRefreshSignal || !rangoVisibleRef.current) return;
    getAppointments(rangoVisibleRef.current)
      .then(setCitas)
      .catch((err) => console.error('Error refrescando citas:', err));
  }, [turnosRefreshSignal]);

  // ── Mapear citas → eventos FullCalendar ──
  const eventos = citas.map(c => {
    const tieneHora = Boolean(c.time);
    const durMin = c.duration || 60;
    const start = tieneHora ? `${c.date}T${c.time}` : c.date;
    const end = tieneHora ? calcularHoraFin(c.date, c.time.slice(0, 5), durMin) : undefined;
    return {
      id: String(c.id),
      title: c.patient_name || c.title || 'Sin nombre',
      start,
      end,
      allDay: !tieneHora,
      backgroundColor: c.is_event ? '#e67e22' : '#2980b9',
      borderColor: 'transparent',
      textColor: '#fff',
      extendedProps: { citaId: c.id, patientId: c.patient, isEvent: c.is_event, notes: c.notes, patientName: c.patient_name, duration: durMin },
    };
  });

  // ── Drag & drop ──
  const handleEventDrop = async (info) => {
    const cita = citas.find(c => String(c.id) === info.event.id);
    if (!cita) { info.revert(); return; }

    const oldDate = cita.date;
    const oldTime = cita.time ? cita.time.slice(0, 5) : null;
    const newDate = info.event.startStr.slice(0, 10);
    const newTime = info.event.allDay
      ? null
      : (info.event.startStr.length > 10 ? info.event.startStr.slice(11, 16) : null);

    if (oldDate === newDate && oldTime === newTime) return;

    const patientId = cita.patient;
    if (!patientId) {
      try {
        const payload = { date: newDate };
        if (oldTime !== newTime) payload.time = newTime;
        const updated = await updateAppointment(cita.id, payload);
        setCitas(prev => prev.map(c => c.id === cita.id ? updated : c));
      } catch { info.revert(); }
      return;
    }

    info.revert();

    let todosDelPaciente;
    try { todosDelPaciente = await acciones.fetchTurnosPaciente(patientId); }
    catch { setErrorCita('Error al verificar turnos del paciente.'); return; }

    const futuros = todosDelPaciente.filter(c => c.id !== cita.id && c.date >= oldDate);
    const todos = todosDelPaciente.filter(c => c.id !== cita.id);

    if (futuros.length === 0) {
      try {
        const payload = { date: newDate };
        if (oldTime !== newTime) payload.time = newTime;
        const updated = await updateAppointment(cita.id, payload);
        setCitas(prev => prev.map(c => c.id === cita.id ? updated : c));
      } catch (err) { setErrorCita('Error al mover el turno: ' + err.message); }
      return;
    }

    setDragPendiente({
      citaId: cita.id, oldDate, oldTime, newDate, newTime,
      patientId, futuros, todos,
      nombrePaciente: cita.patient_name || 'este paciente',
    });
  };

  // ── Resize ──
  const handleEventResize = async (info) => {
    const cita = citas.find(c => String(c.id) === info.event.id);
    if (!cita) { info.revert(); return; }

    const nuevaDuracion = Math.max(15, Math.round((info.event.end - info.event.start) / (1000 * 60)));

    const patientId = cita.patient;
    if (!patientId) {
      try {
        const updated = await updateAppointment(cita.id, { duration: nuevaDuracion });
        setCitas(prev => prev.map(c => c.id === cita.id ? updated : c));
      } catch { info.revert(); }
      return;
    }

    info.revert();

    let todosDelPaciente;
    try { todosDelPaciente = await acciones.fetchTurnosPaciente(patientId); }
    catch { setErrorCita('Error al verificar turnos del paciente.'); return; }

    const futuros = todosDelPaciente.filter(c => c.id !== cita.id && c.date >= cita.date);
    const todos = todosDelPaciente.filter(c => c.id !== cita.id);

    if (futuros.length === 0) {
      try {
        const updated = await updateAppointment(cita.id, { duration: nuevaDuracion });
        setCitas(prev => prev.map(c => c.id === cita.id ? updated : c));
      } catch (err) { setErrorCita('Error al actualizar duración: ' + err.message); }
      return;
    }

    setResizePendiente({
      citaId: cita.id, nuevaDuracion, futuros, todos,
      nombrePaciente: cita.patient_name || 'este paciente',
    });
  };

  // ── Selectable: pre-rellenar formulario al arrastrar ──
  const handleSelect = (info) => {
    const fecha = info.startStr.slice(0, 10);
    const hora = info.allDay ? '' : (info.startStr.length > 10 ? info.startStr.slice(11, 16) : '');
    const duracion = info.allDay ? 60 : Math.max(15, Math.round((info.end - info.start) / (1000 * 60)));
    setDiaSeleccionado(fecha);
    setPrellenado({ hora, duracion, ts: Date.now() });
  };

  // ── Click en celda del calendario ──
  const handleDateClick = (info) => {
    setDiaSeleccionado(info.dateStr.slice(0, 10));
    setPopover(null);
  };

  // ── Click en evento → popover ──
  const handleEventClick = (info) => {
    info.jsEvent.stopPropagation();
    const id = Number(info.event.id);
    const cita = citas.find(c => c.id === id);
    if (!cita) return;
    const rect = info.el.getBoundingClientRect();
    const x = Math.min(rect.left, window.innerWidth - 285);
    const y = Math.min(rect.bottom + 6, window.innerHeight - 220);
    setPopover({ cita, x, y });
  };

  // ── Helpers para aplicar cambios en serie (drag/resize) ──
  const aplicarMovimiento = async (turnosList, deltaDias, deltaMin) => {
    return await Promise.all(
      turnosList.map(c => {
        const d = new Date(c.date + 'T12:00:00');
        d.setDate(d.getDate() + deltaDias);
        const nuevaFecha = d.toISOString().split('T')[0];
        const cTime = c.time ? c.time.slice(0, 5) : null;
        const payload = { date: nuevaFecha };
        if (cTime !== null) payload.time = deltaMin !== 0 ? addMinutesToTime(cTime, deltaMin) : cTime;
        return updateAppointment(c.id, payload);
      })
    );
  };

  const aplicarDuracion = async (ids, nuevaDuracion) => {
    const actualizados = await Promise.all(ids.map(id => updateAppointment(id, { duration: nuevaDuracion })));
    const mapa = Object.fromEntries(actualizados.filter(Boolean).map(c => [c.id, c]));
    setCitas(prev => prev.map(c => mapa[c.id] || c));
  };

  // ── Opciones mover (drag) ──
  const moverSoloEste = async () => {
    if (!dragPendiente) return;
    const { citaId, newDate, oldTime, newTime } = dragPendiente;
    try {
      const payload = { date: newDate };
      if (oldTime !== newTime) payload.time = newTime;
      const updated = await updateAppointment(citaId, payload);
      setCitas(prev => prev.map(c => c.id === citaId ? updated : c));
    } catch (err) { setErrorCita('Error: ' + err.message); }
    setDragPendiente(null);
  };

  const moverEstoYSiguientes = async () => {
    if (!dragPendiente) return;
    const { citaId, oldDate, oldTime, newDate, newTime, futuros } = dragPendiente;
    const deltaDias = Math.round((new Date(newDate) - new Date(oldDate)) / (1000 * 60 * 60 * 24));
    const deltaMin = (oldTime && newTime) ? timeToMinutes(newTime) - timeToMinutes(oldTime) : 0;
    const citaActual = citas.find(c => c.id === citaId) || { id: citaId, date: oldDate, time: oldTime ? oldTime + ':00' : null };
    try {
      const actualizados = await aplicarMovimiento([citaActual, ...futuros], deltaDias, deltaMin);
      const mapa = Object.fromEntries(actualizados.filter(Boolean).map(c => [c.id, c]));
      setCitas(prev => prev.map(c => mapa[c.id] || c));
    } catch (err) { setErrorCita('Error: ' + err.message); }
    setDragPendiente(null);
  };

  const moverTodaLaSerie = async () => {
    if (!dragPendiente) return;
    const { citaId, oldDate, oldTime, newDate, newTime, todos } = dragPendiente;
    const deltaDias = Math.round((new Date(newDate) - new Date(oldDate)) / (1000 * 60 * 60 * 24));
    const deltaMin = (oldTime && newTime) ? timeToMinutes(newTime) - timeToMinutes(oldTime) : 0;
    const citaActual = citas.find(c => c.id === citaId) || { id: citaId, date: oldDate, time: oldTime ? oldTime + ':00' : null };
    try {
      const actualizados = await aplicarMovimiento([citaActual, ...todos], deltaDias, deltaMin);
      const mapa = Object.fromEntries(actualizados.filter(Boolean).map(c => [c.id, c]));
      setCitas(prev => prev.map(c => mapa[c.id] || c));
    } catch (err) { setErrorCita('Error: ' + err.message); }
    setDragPendiente(null);
  };

  // ── Opciones resize ──
  const redimensionarSoloEste = async () => {
    if (!resizePendiente) return;
    try { await aplicarDuracion([resizePendiente.citaId], resizePendiente.nuevaDuracion); }
    catch (err) { setErrorCita('Error: ' + err.message); }
    setResizePendiente(null);
  };

  const redimensionarEstoYSiguientes = async () => {
    if (!resizePendiente) return;
    const ids = [resizePendiente.citaId, ...resizePendiente.futuros.map(f => f.id)];
    try { await aplicarDuracion(ids, resizePendiente.nuevaDuracion); }
    catch (err) { setErrorCita('Error: ' + err.message); }
    setResizePendiente(null);
  };

  const redimensionarTodaLaSerie = async () => {
    if (!resizePendiente) return;
    const ids = [resizePendiente.citaId, ...resizePendiente.todos.map(f => f.id)];
    try { await aplicarDuracion(ids, resizePendiente.nuevaDuracion); }
    catch (err) { setErrorCita('Error: ' + err.message); }
    setResizePendiente(null);
  };

  const formatDiaSeleccionado = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return `${parseInt(d)} de ${MESES[parseInt(m) - 1]} de ${y}`;
  };

  const citasDelDia = diaSeleccionado
    ? ordenarTurnosPorHorario(citas.filter(c => c.date === diaSeleccionado))
    : [];

  return (
    <div className="calendario-wrapper">
      <div className="fc-wrapper">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={esLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          events={eventos}
          editable
          droppable
          selectable
          selectMirror
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          select={handleSelect}
          dateClick={handleDateClick}
          datesSet={handleDatesSet}
          dayCellClassNames={(arg) => {
            const cellDate = arg.date.toISOString().slice(0, 10);
            return cellDate === diaSeleccionado ? ['fc-day-selected'] : [];
          }}
          height="auto"
          eventContent={(arg) => (
            <div className="fc-event-inner" title={arg.event.extendedProps.notes || ''}>
              <span className="fc-event-dot" style={{ background: arg.event.backgroundColor }} />
              {arg.event.extendedProps.isEvent
                ? <span className="fc-badge-evento">Evento</span>
                : <span className="fc-badge-turno">Turno</span>
              }
              <span className="fc-event-title">{arg.event.title}</span>
              {!arg.event.allDay && arg.event.startStr?.length > 10 && (
                <span className="fc-event-hora">{arg.event.startStr.slice(11, 16)}</span>
              )}
            </div>
          )}
          eventClick={handleEventClick}
          dayMaxEvents={3}
          moreLinkContent={(args) => `+${args.num} más`}
        />
      </div>

      {/* Panel del día seleccionado */}
      {diaSeleccionado && (
        <div className="cal-panel-dia">
          <h3 className="cal-panel-titulo">{formatDiaSeleccionado(diaSeleccionado)}</h3>

          {citasDelDia.length === 0 ? (
            <p className="sesiones-vacio">No hay turnos ni eventos para este día.</p>
          ) : (
            <div className="cal-citas-lista">
              {citasDelDia.map(c => (
                <div key={c.id} className={`cal-cita-item ${c.is_event ? 'cal-cita-item-evento' : ''}`}>
                  <div className="cal-cita-info">
                    <span className="cal-cita-tipo-badge">{c.is_event ? 'Evento' : 'Turno'}</span>
                    <span className="cal-cita-nombre">{c.patient_name}</span>
                    {c.time && <span className="cal-cita-hora">{c.time.slice(0, 5)}</span>}
                    {c.notes && <span className="cal-cita-notas">{c.notes}</span>}
                  </div>
                  <button className="btn-eliminar-campo" onClick={() => acciones.iniciarCancelar(c.id)} disabled={acciones.cancelando}>✕</button>
                </div>
              ))}
            </div>
          )}

          {errorCita && <div className="cal-error">{errorCita}</div>}

          <FormularioTurno
            pacientes={pacientes}
            fecha={diaSeleccionado}
            prellenado={prellenado}
            onCreado={(cita) => setCitas(prev => [...prev, cita])}
          />
        </div>
      )}

      {/* Popover rápido */}
      {popover && (
        <TurnoPopover
          cita={popover.cita}
          x={popover.x}
          y={popover.y}
          onCerrar={() => setPopover(null)}
          onEditar={(cita) => { acciones.abrirEdicion(cita); setPopover(null); }}
          onVerFicha={onVerFicha ? (patientId) => { onVerFicha(patientId); setPopover(null); } : null}
          onCancelar={(id) => { acciones.iniciarCancelar(id); setPopover(null); }}
          onRenombrar={(cita) => { acciones.iniciarRenombrar(cita); setPopover(null); }}
          cancelando={acciones.cancelando}
        />
      )}

      {/* Modales de edición/cancelación/renombrado de turnos (compartidos con PendientesDelDia) */}
      <TurnoModals acciones={acciones} />

      {/* Modal 3 opciones: mover (drag) */}
      {dragPendiente && (
        <Modal3Opciones
          titulo="Mover turno"
          mensaje={<>¿A qué turnos querés aplicar el cambio de <strong>{dragPendiente.nombrePaciente}</strong>?</>}
          cantFuturos={dragPendiente.futuros.length}
          cantTodos={dragPendiente.todos.length}
          onCancelar={() => setDragPendiente(null)}
          onSoloEste={moverSoloEste}
          onEstoYSiguientes={moverEstoYSiguientes}
          onTodaLaSerie={moverTodaLaSerie}
        />
      )}

      {/* Modal 3 opciones: redimensionar */}
      {resizePendiente && (
        <Modal3Opciones
          titulo="Cambiar duración"
          mensaje={<>¿A qué turnos querés aplicar el cambio de <strong>{resizePendiente.nombrePaciente}</strong>?</>}
          cantFuturos={resizePendiente.futuros.length}
          cantTodos={resizePendiente.todos.length}
          onCancelar={() => setResizePendiente(null)}
          onSoloEste={redimensionarSoloEste}
          onEstoYSiguientes={redimensionarEstoYSiguientes}
          onTodaLaSerie={redimensionarTodaLaSerie}
        />
      )}
    </div>
  );
}

export default Calendario;
